import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MapIntegration from '../components/MapIntegration';
import POIDetailsSidePanel from '../components/POIDetailsSidePanel';
import PublicItineraryModal from '../components/PublicItineraryModal';
import FloatingTabs from '../components/FloatingTabs';
import MobileAppBanner from '../components/MobileAppBanner';
import styled from 'styled-components';
import 'react-day-picker/dist/style.css';
import { getAllPublicItineraries } from '../services/api';
import cacheService from '../services/cacheService';
import VerticalAdBanner from '../components/AdPlaceholder';
import Toast from '../components/Toast';
import { useLanguage } from '../contexts/LanguageContext';
import FilterComponent from '../components/FilterComponent';

const PageContainer = styled.div`
  position: relative;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const NavbarWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`;

const MapContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const RightAdBanner = styled.div`
  position: fixed;
  top: 160px;
  right: 20px;
  z-index: 999;
  pointer-events: auto;
  
  @media (max-width: 1200px) {
    display: none;
  }
`;

function HomePage() {
  const history = useHistory();
  const location = useLocation();
  const [publicItineraries, setPublicItineraries] = useState([]);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '', link: null });
  const { language } = useLanguage();

  useEffect(() => {
    const fetchPublicItineraries = async () => {
      try {
        // Use cache service for public itineraries
        const data = await cacheService.getPublicItineraries();
        setPublicItineraries(data);
      } catch (error) {
        console.error('Error fetching public itineraries:', error);
      }
    };

    fetchPublicItineraries();
  }, []);

  const handlePOIClick = (poi) => {
    setSelectedPOI(poi);
  };

  const handleClosePOIDetails = () => {
    setSelectedPOI(null);
  };

  const handleAddToTrip = () => {
    // Handle adding the selected POI to a trip
    console.log('Adding POI to trip:', selectedPOI);
  };

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
    // Update URL parameters
    const params = new URLSearchParams(location.search);
    if (filters.length > 0) {
      params.set('filters', filters.join(','));
    } else {
      params.delete('filters');
    }
    history.push({ search: params.toString() });
  };

  // Parse filters from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filters');
    if (filterParam) {
      const filters = filterParam.split(',').map(Number).filter(f => !isNaN(f));
      setActiveFilters(filters);
    }
  }, [location.search]);

  const handleItinerarySelect = (itinerary) => {
    setSelectedItinerary(itinerary);
  };

  const handleToast = (message, type = 'success', link = null) => {
    setToast({
      show: true,
      message,
      type,
      link
    });
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  const handleTabChange = (tab) => {
    if (tab === 'tours') {
      history.push('/tours');
    }
  };

  return (
    <PageContainer>
      <NavbarWrapper>
        <Navbar 
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters}
        />
      </NavbarWrapper>
      <MapContainer>
        <MapIntegration 
          onPOIClick={handlePOIClick}
          selectedFilters={activeFilters}
        />
        <RightAdBanner>
          <VerticalAdBanner />
        </RightAdBanner>
      </MapContainer>
      {selectedPOI && (
        <POIDetailsSidePanel 
          poi={selectedPOI}
          onClose={handleClosePOIDetails}
          onAddToTrip={handleAddToTrip}
          onShowToast={handleToast}
        />
      )}
      <FloatingTabs onTabChange={handleTabChange} />
      {selectedItinerary && (
        <PublicItineraryModal
          itinerary={selectedItinerary}
          onClose={() => setSelectedItinerary(null)}
        />
      )}
      {toast.show && (
        <Toast 
          message={toast.message}
          type={toast.type}
          link={toast.link}
          onClose={handleCloseToast}
        />
      )}
      <FilterComponent 
        onFilterChange={handleFilterChange}
        selectedFilters={activeFilters}
        language={language}
      />
      <MobileAppBanner />
    </PageContainer>
  );
}

export default HomePage;
